/**
 * MapLocationList Component
 * The ordered list of everything the block named.
 *
 * This is not a fallback afterthought: a WebGL canvas is opaque to assistive
 * technology and to text selection, so the list renders ALONGSIDE the map at
 * all times and is the surface that carries the block's meaning when the canvas
 * cannot draw. Only an embedder whose own chrome names the same places may ask
 * `MapSurface` not to mount it, and never on a surface where the canvas is
 * absent. Every row is `dir="auto"` — `markdown-hebrew.ts` covers markdown
 * text elements only and never reaches this DOM.
 *
 * What each row LEADS with is decided by `mapLocationRowText`, not here: an
 * unresolved row leads with its place, because naming the place that could not
 * be placed is the entire reason this surface exists.
 *
 * An authored description renders as its own line under the row, never beside
 * the resolution detail: one is prose the author wrote, the other is the
 * system saying where the pin is or why it has nowhere to be, and a row must
 * be able to carry both at once.
 */

'use client';

import { useEffect, useRef, type CSSProperties, type ReactElement } from 'react';
import { useReducedMotion } from 'framer-motion';
import { ButtonPreset } from '@/constants/componentPresets';
import { Cbutton } from '../Cbutton';
import { getFontFamily } from '@/theme/typography';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import { hasMapPinIcon, mapPinIconForKeyword } from './map-pin-icon';
import {
  isAmbiguousEntry,
  isCoordinateDetailRow,
  mapLocationRowText,
  type MapLocationEntry,
} from './map-render-model';

/** Semantic row-part hooks for stage chromes (e.g. the Atlas family's accent
 *  place-detail read) — styling only, never behaviour. */
export enum MapLocationRowPart {
  Index = 'index',
  Icon = 'icon',
  Primary = 'primary',
  Secondary = 'secondary',
  Detail = 'detail',
  Description = 'description',
}

export enum MapLocationListVariant {
  /** Inside the expanded canvas or the modal. */
  Canvas = 'canvas',
  // Compact retired — the collapsed 317 tile bakes place names into the
  // MapCompactPreview composition and never mounts this list.
}

const ROOT_STYLE_BY_VARIANT: Record<MapLocationListVariant, CSSProperties> = {
  [MapLocationListVariant.Canvas]: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    maxHeight: 150,
    overflowY: 'auto',
    flexShrink: 0,
  },
};

/** `flexWrap` is what gives the description its own line: the row parts stay
 *  DIRECT children of the `li` (the stage chromes in MapExpandedSurface.css
 *  restyle `li` and select `[data-map-row-part]` from it, and one of them
 *  stacks the parts itself), so the description takes a full flex basis and
 *  wraps under the row rather than being nested inside a wrapper the chromes
 *  cannot see. */
const ITEM_STYLE_BY_VARIANT: Record<MapLocationListVariant, CSSProperties> = {
  [MapLocationListVariant.Canvas]: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 8,
    rowGap: 2,
    padding: '5px 8px',
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.03)',
    fontFamily: getFontFamily(),
    fontSize: 12,
    color: 'rgba(255,255,255,0.86)',
    minWidth: 0,
  },
};

const INDEX_STYLE: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  color: '#66FEC1',
  opacity: 0.85,
  flexShrink: 0,
};

/** The place-kind glyph beside the index — the same glyph the pin carries, so
 *  a card and its marker read as one place. Rendered only for a keyword that
 *  RESOLVES: the fallback pin glyph beside every row would be decoration. */
const ICON_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignSelf: 'center',
  color: 'rgba(102, 254, 193, 0.8)',
  flexShrink: 0,
};

const ROW_ICON_SIZE = 12;
const ROW_ICON_STROKE_WIDTH = 2.2;

/** Bidi direction for row text, which carries arbitrary-script place names. */
const AUTO_TEXT_DIRECTION = 'auto';

/** How the rail brings the chosen card to the reader: centred along its own
 *  inline axis, `nearest` on the block axis so a page-context list is never
 *  yanked vertically. */
const ACTIVE_ROW_SELECTOR = '[data-map-row-active]';
const ACTIVE_ROW_SCROLL_ALIGNMENT: ScrollIntoViewOptions = {
  block: 'nearest',
  inline: 'center',
};
const SMOOTH_SCROLL: ScrollBehavior = 'smooth';
const INSTANT_SCROLL: ScrollBehavior = 'auto';

const PRIMARY_STYLE: CSSProperties = {
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};

/** The label beside an unresolved place. Present but subordinate: the place is
 *  what identifies the row, the label is what the author called it. */
const SECONDARY_STYLE: CSSProperties = {
  fontWeight: 400,
  color: 'rgba(255,255,255,0.5)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};

const DETAIL_STYLE: CSSProperties = {
  marginInlineStart: 'auto',
  color: 'rgba(255,255,255,0.45)',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  paddingInlineStart: 8,
};

const UNRESOLVED_DETAIL_STYLE: CSSProperties = {
  ...DETAIL_STYLE,
  color: 'rgba(255,255,255,0.32)',
  fontStyle: 'italic',
};

/**
 * An ambiguous row's trailing text names the rival places, so it is a phrase
 * rather than a two-word status. The other details are short and pinned right;
 * this one is allowed to wrap, because that beats pushing the place name it
 * belongs to off the row.
 */
const AMBIGUOUS_DETAIL_STYLE_BY_VARIANT: Record<MapLocationListVariant, CSSProperties> = {
  [MapLocationListVariant.Canvas]: {
    ...UNRESOLVED_DETAIL_STYLE,
    whiteSpace: 'normal',
    textAlign: 'end',
    flexShrink: 1,
    minWidth: 0,
  },
};

/**
 * The author's prose about the place, on its own line under the row.
 *
 * A full flex basis is what breaks the line, and it sits AFTER the detail in
 * source order so the row still reads "what / where" before "about". It wraps
 * (this is prose, not a status) but is clamped, because one long description
 * must not push the rest of the list out of a 150px scroller.
 */
const DESCRIPTION_STYLE_BY_VARIANT: Record<MapLocationListVariant, CSSProperties> = {
  [MapLocationListVariant.Canvas]: {
    flexBasis: '100%',
    minWidth: 0,
    fontSize: 11,
    fontWeight: 400,
    lineHeight: 1.4,
    color: 'rgba(255,255,255,0.55)',
    whiteSpace: 'normal',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    overflow: 'hidden',
  },
};

export interface MapLocationListProps {
  dataElScope: DataElScope;
  entries: MapLocationEntry[];
  variant: MapLocationListVariant;
  /** Whether a coordinate pin's row trails with its own lat/lon. False only
   *  where the embedder prints that pair itself. Never touches a place row's
   *  detail or an unresolved row's verdict — those repeat nothing. */
  showCoordinateDetail: boolean;
  /** When set, positioned rows become focus buttons (tweak hdz7squ): click
   *  or Enter/Space asks the host to fly the map to that pin. */
  onEntryClick?: (entry: MapLocationEntry) => void;
  /** The row whose pin currently carries the chosen cue. */
  activeEntryKey?: string | null;
}

function detailStyleFor({
  entry,
  variant,
}: {
  entry: MapLocationEntry;
  variant: MapLocationListVariant;
}): CSSProperties {
  if (entry.position) {
    return DETAIL_STYLE;
  }

  return isAmbiguousEntry(entry)
    ? AMBIGUOUS_DETAIL_STYLE_BY_VARIANT[variant]
    : UNRESOLVED_DETAIL_STYLE;
}

export function MapLocationList({
  dataElScope,
  entries,
  variant,
  showCoordinateDetail,
  onEntryClick,
  activeEntryKey = null,
}: MapLocationListProps): ReactElement {
  const listRef = useRef<HTMLOListElement | null>(null);
  const shouldReduceMotion = useReducedMotion() === true;

  // A pin picked on the MAP chooses its card here, and the chosen card may be
  // scrolled out of the rail — so the rail comes to it, not the reader. The
  // active row is found by the attribute the same render just stamped;
  // `nearest` on the block axis keeps a page-context list from yanking the
  // page while the rail's own inline axis centres the card.
  useEffect(() => {
    if (activeEntryKey == null) {
      return;
    }
    const activeRow = listRef.current?.querySelector(ACTIVE_ROW_SELECTOR);
    activeRow?.scrollIntoView({
      ...ACTIVE_ROW_SCROLL_ALIGNMENT,
      behavior: shouldReduceMotion ? INSTANT_SCROLL : SMOOTH_SCROLL,
    });
  }, [activeEntryKey, shouldReduceMotion]);

  return (
      <div data-el={dataEl(dataElScope, 'MapLocationList/Root')} style={{ minWidth: 0 }}>
        <ol data-el={dataEl(dataElScope, 'MapLocationList/Viewport')} ref={listRef} style={ROOT_STYLE_BY_VARIANT[variant]}>
          {entries.map((entry, index) => {
            const row = mapLocationRowText(entry);
            const detailText =
              showCoordinateDetail || !isCoordinateDetailRow(entry) ? row.detail : null;
            const isInteractive = onEntryClick != null && entry.position != null;
            const activate = (): void => onEntryClick?.(entry);
            // Only a RESOLVED keyword draws in the card; the pin's fallback
            // glyph beside every row would be decoration, not data.
            const RowIcon = hasMapPinIcon(entry.icon)
              ? mapPinIconForKeyword(entry.icon)
              : null;

            return (
              <li
                key={entry.key}
                style={{ display: 'contents' }}
              >
              <Cbutton
                preset={ButtonPreset.Ghost}
                ariaLabel={isInteractive ? row.primary : undefined}
                padding={0}
                onClick={isInteractive ? activate : undefined}
                disableHoverEffects={!isInteractive}
                rootAttributes={{
                  'data-el': dataEl(dataElScope, 'MapLocationList/Entry'),
                  dir: AUTO_TEXT_DIRECTION,
                  'data-map-row': true,
                  'data-map-row-interactive': isInteractive ? true : undefined,
                  'data-map-row-active': isInteractive && entry.key === activeEntryKey ? true : undefined,
                }}
                style={{
                  ...ITEM_STYLE_BY_VARIANT[variant],
                }}
                containerStyle={{ display: 'contents' }}
                contentStyle={{ display: 'contents' }}
              >
                <span
                  data-map-row-part={MapLocationRowPart.Index}
                  style={INDEX_STYLE}
                >
                  {index + 1}
                </span>
                {RowIcon ? (
                  <span
                    aria-hidden
                    data-map-row-part={MapLocationRowPart.Icon}
                    style={ICON_STYLE}
                  >
                    <RowIcon
                      size={ROW_ICON_SIZE}
                      strokeWidth={ROW_ICON_STROKE_WIDTH}
                    />
                  </span>
                ) : null}
                <span
                  data-map-row-part={MapLocationRowPart.Primary}
                  dir={AUTO_TEXT_DIRECTION}
                  style={PRIMARY_STYLE}
                >
                  {row.primary}
                </span>
                {row.secondary ? (
                  <span
                    data-map-row-part={MapLocationRowPart.Secondary}
                    dir={AUTO_TEXT_DIRECTION}
                    style={SECONDARY_STYLE}
                  >
                    {row.secondary}
                  </span>
                ) : null}
                {detailText ? (
                  <span
                    data-map-row-part={MapLocationRowPart.Detail}
                    dir={AUTO_TEXT_DIRECTION}
                    style={detailStyleFor({ entry, variant })}
                  >
                    {detailText}
                  </span>
                ) : null}
                {row.description ? (
                  <span
                    data-map-row-part={MapLocationRowPart.Description}
                    dir={AUTO_TEXT_DIRECTION}
                    style={DESCRIPTION_STYLE_BY_VARIANT[variant]}
                  >
                    {row.description}
                  </span>
                ) : null}
              </Cbutton>
              </li>
            );
          })}
        </ol>
      </div>
  );
}
